import {
  AI_FEATURE_KEYS,
  AI_FEATURES,
  type AiFeatureKey,
  type AiFeatureOverride,
  type AiModel,
  type AiReasoningEffort,
  type PlatformAiSettings,
  type TranscriptionModel,
  type UpdatePlatformAiSettingsRequest
} from '@bliks-io/contracts';
import { diffPendingEdits, type PendingEdits, type StoredValues } from './app-model-rows';

type FeatureOverrideMap = Partial<Record<AiFeatureKey, AiFeatureOverride | null>>;

export type FormOverrides = Partial<{
  highModel: AiModel;
  lowModel: AiModel;
  transcriptionModel: TranscriptionModel;
  featureOverrides: FeatureOverrideMap;
  appModelOverrides: PendingEdits<AiFeatureOverride>;
  // Keyed by tier, by feature key and by app override key.
  tierReasoningEfforts: PendingEdits<AiReasoningEffort>;
  featureReasoningEfforts: PendingEdits<AiReasoningEffort>;
  appReasoningEfforts: PendingEdits<AiReasoningEffort>;
}>;

export type EffortField =
  | 'tierReasoningEfforts'
  | 'featureReasoningEfforts'
  | 'appReasoningEfforts';

export function storedReasoningEfforts(
  settings: PlatformAiSettings | undefined
): Record<EffortField, StoredValues<AiReasoningEffort>> {
  return {
    tierReasoningEfforts: {
      high: settings?.highReasoningEffort,
      low: settings?.lowReasoningEffort
    },
    featureReasoningEfforts: settings?.featureReasoningEfforts ?? {},
    appReasoningEfforts: settings?.appReasoningEfforts ?? {}
  };
}

export const editEffort = (
  edits: FormOverrides,
  field: EffortField,
  key: string,
  effort: AiReasoningEffort | null
): FormOverrides => ({ ...edits, [field]: { ...edits[field], [key]: effort } });

/** Efforts diff raw-to-raw like the app models; a tier's goes out under its own field. */
function diffReasoningEfforts(
  overrides: FormOverrides,
  settings: PlatformAiSettings
): UpdatePlatformAiSettingsRequest {
  const stored = storedReasoningEfforts(settings);
  const tierEfforts = diffPendingEdits(
    overrides.tierReasoningEfforts ?? {},
    stored.tierReasoningEfforts
  );
  const featureReasoningEfforts = diffPendingEdits(
    overrides.featureReasoningEfforts ?? {},
    stored.featureReasoningEfforts
  );
  const appReasoningEfforts = diffPendingEdits(
    overrides.appReasoningEfforts ?? {},
    stored.appReasoningEfforts
  );
  return {
    ...('high' in tierEfforts && { highReasoningEffort: tierEfforts.high }),
    ...('low' in tierEfforts && { lowReasoningEffort: tierEfforts.low }),
    ...(Object.keys(featureReasoningEfforts).length > 0 && { featureReasoningEfforts }),
    ...(Object.keys(appReasoningEfforts).length > 0 && { appReasoningEfforts })
  };
}

/** The save payload: every pending edit that changes a stored value. */
export function diffSettings(
  overrides: FormOverrides,
  settings: PlatformAiSettings
): UpdatePlatformAiSettingsRequest {
  const result = diffReasoningEfforts(overrides, settings);
  if (overrides.highModel !== undefined && overrides.highModel !== settings.highModel) {
    result.highModel = overrides.highModel;
  }
  if (overrides.lowModel !== undefined && overrides.lowModel !== settings.lowModel) {
    result.lowModel = overrides.lowModel;
  }
  if (
    overrides.transcriptionModel !== undefined &&
    overrides.transcriptionModel !== settings.transcriptionModel
  ) {
    result.transcriptionModel = overrides.transcriptionModel;
  }
  const featureOverrides: FeatureOverrideMap = {};
  for (const key of AI_FEATURE_KEYS) {
    const pending = overrides.featureOverrides?.[key];
    if (pending === undefined) continue;
    const baseline = settings.featureOverrides?.[key] ?? AI_FEATURES[key].defaultTier;
    const pendingResolved = pending ?? AI_FEATURES[key].defaultTier;
    if (pendingResolved !== baseline) featureOverrides[key] = pending;
  }
  if (Object.keys(featureOverrides).length > 0) {
    result.featureOverrides = featureOverrides;
  }
  const appModelOverrides = diffPendingEdits(
    overrides.appModelOverrides ?? {},
    settings.appModelOverrides ?? {}
  );
  if (Object.keys(appModelOverrides).length > 0) {
    result.appModelOverrides = appModelOverrides;
  }
  return result;
}
